import { cn } from "@/lib/utils";
import { AvatarBorder } from "@/components/AvatarBorder";
import type {
  GachaClaimItem,
  GachaHistoryItem,
  GachaPrizeItem,
  InventoryItem,
  ItemDetail,
  ItemType,
  RewardsItems,
  Shop,
} from "@/lib/types/api";

// Placeholder icon component for items without images
const PlaceholderIcon = ({ className }: { className?: string }) => (
  <div className={cn("bg-gray-400 rounded", className)} />
);

type ShopSubItem = NonNullable<Shop["itemData"]>[number];

type ItemIconItem =
  | Shop
  | ShopSubItem
  | InventoryItem
  | GachaPrizeItem
  | GachaClaimItem
  | ItemDetail
  | GachaHistoryItem
  | RewardsItems;

type ItemIconProps = {
  item: ItemIconItem;
  className?: string;
  userProfilePicture?: string;
  isDisplay?: boolean;
  trophyClassName?: string;
  size?: number;
};

const resolveImageUrl = (item: ItemIconItem) => {
  if ("imageUrl" in item) {
    return item.imageUrl;
  }
  if ("itemImage" in item) {
    return item.itemImage;
  }
  return undefined;
};

const resolveCategory = (item: ItemIconItem) => {
  if ("category" in item) {
    return item.category;
  }
  if ("itemCategory" in item) {
    return item.itemCategory;
  }
  return undefined;
};

const resolveChartBase = (item: ItemIconItem) => {
  let cosmeticChartBaseColor: string | undefined;
  let cosmeticChartBaseType: string | undefined;

  if ("itemData" in item && item.itemData && !Array.isArray(item.itemData)) {
    const itemData = item.itemData as {
      cosmeticChartBaseColor?: string;
      cosmeticChartBaseType?: string;
      cosmetic?: {
        cosmeticChartBaseColor?: string;
        cosmeticChartBaseType?: string;
      };
    };

    if (itemData.cosmeticChartBaseColor) {
      cosmeticChartBaseColor = itemData.cosmeticChartBaseColor;
      cosmeticChartBaseType = itemData.cosmeticChartBaseType;
    } else if (itemData.cosmetic) {
      cosmeticChartBaseColor = itemData.cosmetic.cosmeticChartBaseColor;
      cosmeticChartBaseType = itemData.cosmetic.cosmeticChartBaseType;
    }
  }

  if ("itemBaseColor" in item) {
    cosmeticChartBaseColor =
      (item as { itemBaseColor?: string }).itemBaseColor ??
      cosmeticChartBaseColor;
  }
  if ("itemBaseType" in item) {
    cosmeticChartBaseType =
      (item as { itemBaseType?: string }).itemBaseType ?? cosmeticChartBaseType;
  }

  return { cosmeticChartBaseColor, cosmeticChartBaseType };
};

// Item icon component that handles both image URLs and placeholder
export const ItemIcon = ({
  item,
  className,
  userProfilePicture,
  isDisplay = true,
  size,
  trophyClassName,
}: ItemIconProps) => {
  const imageUrl = resolveImageUrl(item);
  const category = resolveCategory(item);
  const { cosmeticChartBaseColor, cosmeticChartBaseType } =
    resolveChartBase(item);
  const resolvedItemId =
    "itemId" in item ? item.itemId : "id" in item ? item.id : undefined;
  const isRegularItemOverride =
    resolvedItemId === "a0ccbca8-7fd5-4b86-9ec5-efe063f76404" ||
    resolvedItemId === "47d5ef1f-cf01-4eae-a132-681bab6c2bc5";
  const itemType: ItemType = item.itemType;
  const isCosmetics =
    !isRegularItemOverride &&
    (category === "cosmetics" || itemType === "chart-frame");
  const isItemInChartFrame = itemType !== "frame";
  const isItemDoubleTrophies = itemType === "double-trophies";
  const isReferralItem =
    itemType === "referral-link" || itemType === "referral-code";

  if (isReferralItem && !isRegularItemOverride) {
    return (
      <div className="relative inline-flex items-center justify-center">
        <img
          src={"https://cdn.navalabs.io/components/gacha/coupon_6SsA9g.png"}
          alt="Gacha Coupon"
          className="absolute w-full scale-150 -rotate-[12.5deg]"
          style={{
            imageRendering: "pixelated",
          }}
        />
        {imageUrl && imageUrl.trim() !== "" ? (
          <img
            src={imageUrl}
            alt="Item"
            className={cn(
              className,
              "pointer-events-none z-10 -rotate-[12.5deg] drop-shadow-[0_0.5px_0px_#000,1px_0.5px_0px_#000,-1px_0.5px_0px_#000]",
              trophyClassName,
            )}
          />
        ) : (
          <PlaceholderIcon className={className} />
        )}
      </div>
    );
  }

  // Render chart SVG if imageUrl is null/undefined/empty AND we have chart props
  const hasNoImage = !imageUrl || imageUrl.trim() === "";
  const hasChartProps = cosmeticChartBaseColor || cosmeticChartBaseType;

  if (hasNoImage && hasChartProps && !isRegularItemOverride) {
    const strokeColor = cosmeticChartBaseColor || "currentColor";
    const dashArray =
      cosmeticChartBaseType === "dotted"
        ? "2,4"
        : cosmeticChartBaseType === "dashed"
          ? "5,6"
          : undefined;

    // Generate a unique filter ID based on stroke color
    const filterId = `glow-${Math.abs(
      strokeColor.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0),
    )}`;

    return (
      <svg
        viewBox="0 0 100 50"
        className={cn("w-full h-full", className)}
        preserveAspectRatio="none"
      >
        <defs>
          <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" />
          </filter>
        </defs>
        {/* Glow layer - wider stroke with blur */}
        <polyline
          points="5,35 15,28 25,32 35,25 45,30 55,27 65,33 75,26 85,31 95,28"
          fill="none"
          stroke={strokeColor}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={dashArray}
          opacity="0.7"
          filter={`url(#${filterId})`}
        />
        {/* Main line */}
        <polyline
          points="5,35 15,28 25,32 35,25 45,30 55,27 65,33 75,26 85,31 95,28"
          fill="none"
          stroke={strokeColor}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={dashArray}
        />
      </svg>
    );
  }

  if (imageUrl && imageUrl.trim() !== "") {
    return (
      <>
        {isCosmetics ? (
          <AvatarBorder
            src={
              userProfilePicture ||
              "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/profile/coach_monke.png"
            }
            decoration={imageUrl}
            size={size || (isDisplay ? 50 : 100)}
            rounded={isItemInChartFrame}
          />
        ) : (
          <img
            src={imageUrl}
            alt="Item"
            className={cn(
              "rounded",
              isRegularItemOverride && "scale-150",
              className,
              trophyClassName,
            )}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            style={
              isItemDoubleTrophies ? { imageRendering: "pixelated" } : undefined
            }
          />
        )}
      </>
    );
  }
  return <PlaceholderIcon className={className} />;
};
